import { EMPTY_COLUMN_VALUE } from 'constants/Common';
import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import { FetchOrderActions as MainActions } from 'store/ducks/orders';
import { Formatter } from 'utils';
import { QRCode } from 'react-qr-code';
import * as S from './styles';

interface Props {
  orderId: string | number;
}

interface Detail {
  label: string;
  value: string | number;
}

export const OrderPrinting: React.FC<Props> = ({ orderId }) => {
  const dispatch: AppDispatch = useDispatch();

  const { data: order, loading } = useSelector(
    (state: RootState) => state.fetchOrder
  );

  const onFetchSuccess = useCallback(() => {
    setTimeout(() => {
      window.print();
    }, 500);
  }, []);

  const onFetchFailure = useCallback(() => {
    setTimeout(() => {
      window.close();
    }, 3000);
  }, []);

  const fetchOrder = useCallback((): void => {
    dispatch(MainActions.request(orderId, onFetchSuccess, onFetchFailure));
  }, [dispatch, onFetchFailure, onFetchSuccess, orderId]);

  const Header = useCallback((): JSX.Element => {
    if (!order) return <></>;

    const logoUrl = order.company.logoUrl;
    const type =
      order?.orderType.name === 'Carregamento'
        ? 'coleta'
        : order?.orderType.name === 'Descarregamento'
          ? 'entrega'
          : '...';
    const orderNumber = `${order.id}`.padStart(4, '0');
    const date = Formatter.date(order.scheduledAt, {
      format: 'dd/MM/yyyy HH:mm',
    });

    return (
      <S.PageHeader>
        {!!logoUrl && (
          <S.CompanyLogo>
            <img src={logoUrl} alt="" />
          </S.CompanyLogo>
        )}
        <div>
          <QRCode
            value={`https://app.agendamento.webcol.systems/app/orders/${order.id}/bip`}
            size={100}
          />
        </div>

        <S.PageHeaderTitle>
          <h4>
            Ordem de {type} {orderNumber}
          </h4>
          <span>{date}</span>
        </S.PageHeaderTitle>
        <S.PageHeaderStatus>
          <span>Status: {Formatter.orderStatusName(order.status)}</span>
          {!!order.cancelationReason && (
            <span>
              Justificativa de cancelamento: {order.cancelationReason.name}
            </span>
          )}
        </S.PageHeaderStatus>
      </S.PageHeader>
    );
  }, [order]);

  const WarehouseGrid = useCallback((): JSX.Element => {
    if (!order) return <></>;
    const { warehouse, dock } = order;

    return (
      <S.WarehouseGrid>
        <S.Detail>
          <S.DetailLabel>Armazém</S.DetailLabel>
          <S.DetailValue>{warehouse.tradeName}</S.DetailValue>
        </S.Detail>
        <S.Detail>
          <S.DetailLabel>Doca</S.DetailLabel>
          <S.DetailValue>{dock.name}</S.DetailValue>
        </S.Detail>
        <S.Detail>
          <S.DetailLabel>Endereço do armazém</S.DetailLabel>
          <S.DetailValue>{Formatter.address(warehouse)}</S.DetailValue>
        </S.Detail>
      </S.WarehouseGrid>
    );
  }, [order]);

  const MainGrid = useCallback((): JSX.Element => {
    if (!order) return <></>;

    const {
      orderItems,
      totalWeight,
      carrier,
      company,
      orderType,
      operationType,
      vehicleType,
      driverName,
      driverDocument,
      vehiclePlate,
      cargoType,
      devolutionNumber,
      exportationSecuritySeal,
      exportationContainer,
      importationOrder,
      importationSequential,
      importationContainer,
      importationDeclaration,
    } = order;

    const getNullableDetail = (
      label: string,
      value: string | number | null
    ): Detail[] => {
      if (value === null) return [];
      return [{ label, value }];
    };

    const details: Detail[] = [
      {
        label: 'Cliente',
        value: company.tradeName,
      },
      {
        label: 'Qtd documentos',
        value: orderItems.length,
      },
      {
        label: 'Peso',
        value: Formatter.weight(totalWeight),
      },
      {
        label: 'Transportadora',
        value: carrier.tradeName,
      },
      {
        label: 'Tipo de ordem',
        value: orderType.name,
      },

      {
        label: 'Motorista',
        value: driverName,
      },
      {
        label: 'CPF do motorista',
        value: Formatter.document(driverDocument),
      },
      {
        label: 'Tipo de veículo',
        value: vehicleType.name,
      },
      {
        label: 'Placa do veículo',
        value: vehiclePlate,
      },
      {
        label: 'Tipo de carga',
        value: cargoType.name,
      },
      {
        label: 'Tipo de operação',
        value: operationType.name,
      },
      ...getNullableDetail('Nº ordem devolução', devolutionNumber),
      ...getNullableDetail('Container', exportationContainer),
      ...getNullableDetail('Lacre', exportationSecuritySeal),
      ...getNullableDetail('Declaração', importationDeclaration),
      ...getNullableDetail('Pedido', importationOrder),
      ...getNullableDetail('Sequencial', importationSequential),
      ...getNullableDetail('Container', importationContainer),
    ];

    return (
      <S.MainGrid>
        {details.map(({ label, value }, i) => (
          <S.Detail key={i}>
            <S.DetailLabel>{label}</S.DetailLabel>
            <S.DetailValue>{value}</S.DetailValue>
          </S.Detail>
        ))}
      </S.MainGrid>
    );
  }, [order]);

  const InfoGrid = useCallback((): JSX.Element => {
    if (!order) return <></>;
    const { observation, material } = order;

    return (
      <S.InfoGrid>
        <S.Detail>
          <S.DetailLabel>Observações</S.DetailLabel>
          <S.DetailValue>{observation || EMPTY_COLUMN_VALUE}</S.DetailValue>
        </S.Detail>
        <S.Detail>
          <S.DetailLabel>Materiais</S.DetailLabel>
          <S.DetailValue>{material || EMPTY_COLUMN_VALUE}</S.DetailValue>
        </S.Detail>
      </S.InfoGrid>
    );
  }, [order]);

  const Events = useCallback((): JSX.Element => {
    if (!order) return <></>;
    const { vehicleArrivedAt, startedAt, finishedAt, vehicleDepartedAt } =
      order;

    const getDatetimeValue = (event: string | null): string => {
      if (!event) {
        return Formatter.date(order.scheduledAt, {
          format: 'dd MMM yyyy, __:__',
        });
      }

      return Formatter.date(event, { format: 'dd MMM yyyy, HH:mm' });
    };

    return (
      <S.EventsGrid>
        <S.Detail>
          <S.DetailLabel>Chegada do veículo</S.DetailLabel>
          <S.DetailValue>{getDatetimeValue(vehicleArrivedAt)}</S.DetailValue>
        </S.Detail>
        <S.Detail>
          <S.DetailLabel>Início da coleta</S.DetailLabel>
          <S.DetailValue>{getDatetimeValue(startedAt)}</S.DetailValue>
        </S.Detail>
        <S.Detail>
          <S.DetailLabel>Fim da coleta</S.DetailLabel>
          <S.DetailValue>{getDatetimeValue(finishedAt)}</S.DetailValue>
        </S.Detail>
        <S.Detail>
          <S.DetailLabel>Saída do veículo</S.DetailLabel>
          <S.DetailValue>{getDatetimeValue(vehicleDepartedAt)}</S.DetailValue>
        </S.Detail>
      </S.EventsGrid>
    );
  }, [order]);

  const OrderItems = useCallback((): JSX.Element => {
    if (!order?.orderItems.length) return <></>;

    return (
      <>
        <S.Subtitle>Itens do agendamento</S.Subtitle>
        <S.OrderItemsGrid>
          <S.ListHeader>
            <div>Documento</div>
            <div>Peso</div>
            <div>Valor</div>
          </S.ListHeader>
          {order.orderItems.map((item, i) => (
            <S.ListItem key={item.id}>
              <S.Column>{item.document}</S.Column>
              <S.Column>{Formatter.weight(item.weight)}</S.Column>
              <S.Column>{Formatter.currency(item.value)}</S.Column>
            </S.ListItem>
          ))}
        </S.OrderItemsGrid>
      </>
    );
  }, [order?.orderItems]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  useEffect(() => {
    return () => {
      dispatch(MainActions.reset());
    };
  }, [dispatch]);

  return (
    <S.PageContainer>
      {loading && <S.ActivityIndicator />}
      <Header />
      <WarehouseGrid />
      <MainGrid />
      <InfoGrid />
      <Events />
      <OrderItems />
    </S.PageContainer>
  );
};
